/// <amd-module name="Favorites/Panel/CrossServiceFavoritesProvider" />
import { Favorite, FavoriteProvider } from "Favorites/Common/Contracts/Favorite";
import { IFavoritesService } from "Favorites/Common/FavoritesService";
import * as FavoriteResources from "Favorites/Common/Resources";
import { IFavoriteRestClient } from "Favorites/Common/RestClient/Favorite";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

/**
 * This class is purpose built for the favorites panel where asynchronous render updates are needed for performance.
 * In normal scenarios, use the Favorites Service.
 */
export class CrossServiceFavoritesProvider {
    public static AreaName = "Favorites";
    public static ScenarioName = "CrossServiceFavoritesLoaded";

    private service: IFavoritesService;

    constructor(private pageContext: IVssPageContext) {
        this.service = this.pageContext.getService<IFavoritesService>("IFavoritesService");
    }

    /**
     * Provider for the favorites panel. Exposes parallel loading of favorites.
     * This is done so that slow or unresponsive services do not delay or block the overall experience.
     */
    public getFavoritesForScope(
        artifactScopeType: string,
        artifactScopeId?: string,
        includeExtendedDetails?: boolean
    ): Promise<Promise<Favorite[]>[]> {
        if (!this.service.canUseFavorites()) {
            return Promise.resolve([]);
        }

        return this.service.getProviders().then((providerMap: { [artifactType: string]: FavoriteProvider }) => {
            const favoritesByServiceInstancePromises: { [serviceInstance: string]: { promise: Promise<Favorite[]>; service: string } } = {};

            for (const artifactType in providerMap) {
                const provider = providerMap[artifactType];
                if (!favoritesByServiceInstancePromises[provider.serviceUri]) {
                    try {
                        const favoritesClient = this.pageContext.getRestClient<IFavoriteRestClient>("IFavoriteRestClient", {
                            serviceInstanceType: provider.serviceIdentifier
                        });
                        const favoritesPromise = favoritesClient.getFavorites(undefined, artifactScopeType, artifactScopeId, includeExtendedDetails);
                        favoritesByServiceInstancePromises[provider.serviceUri] = { promise: favoritesPromise, service: provider.serviceIdentifier };
                    } catch (e) {
                        console.warn(format(FavoriteResources.FavoriteServiceCouldNotBeQueried, provider.serviceUri, e));
                        // For any failed providers return empty set of favorites.
                        // Compass team is following up with User Story #1400053
                        favoritesByServiceInstancePromises[provider.serviceUri] = {
                            promise: Promise.resolve([]),
                            service: provider.serviceIdentifier
                        };
                    }
                }
            }
            const performanceService = this.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
            const dataPromises: Promise<Favorite[]>[] = [];
            const promises = Object.keys(favoritesByServiceInstancePromises).map(k => favoritesByServiceInstancePromises[k]);
            const resolvedServices = new Set();

            const extended = includeExtendedDetails ? "-extended" : "";
            performanceService.startScenario(CrossServiceFavoritesProvider.ScenarioName + extended, false);

            promises.forEach(promiseServiceTuple => {
                dataPromises.push(
                    promiseServiceTuple.promise.then(
                        value => {
                            if (value.length) {
                                resolvedServices.add(promiseServiceTuple.service);
                            }
                            return value;
                        },
                        reason => {
                            // we return an empty array if a federated source does not load properly. This provides a graceful experience to not blow up the UI that depends on getting the
                            // collection of favorites across providers.
                            const telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
                            telemetryService.publishEvent("Favorites", "FavoritesService", reason);
                            return [];
                        }
                    )
                );
            });
            Promise.all(dataPromises).then(favoritesArrays => {
                const resolvedFavoritesTypes = new Set();
                favoritesArrays.forEach(favoritesArray => {
                    favoritesArray.forEach(favorite => {
                        resolvedFavoritesTypes.add(favorite.artifactType);
                    });
                });
                const telemetryProps = {
                    favoritesTypes: Array.from(resolvedFavoritesTypes),
                    favoritesServicesUsed: Array.from(resolvedServices),
                    includeExtendedDetails: includeExtendedDetails
                };
                performanceService.endScenario(
                    CrossServiceFavoritesProvider.AreaName,
                    CrossServiceFavoritesProvider.ScenarioName + extended,
                    false,
                    undefined,
                    telemetryProps
                );
            });

            return dataPromises;
        });
    }
}
